import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCapAlertData } from '../../../actions/capAlerts/capAlertsActionCreators';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import { NUMBER } from '../../../constants/common/number';
import endpoints from '../../../constants/configs/endpoints';
import { environmentSelector } from '../../../selectors/commonSelectors';
import CapAlertModal from './CapAlertModal';

export interface IAlertData {
    hhFirstName: string;
    hhLastName: string;
    needAttention: {
        buttonText: string;
        color: string;
        id: string;
        priority: string;
        status: string;
    }[];
    statues: {
        buttonText: string;
        color: string;
        id: string;
        priority: string;
        status: string;
    }[];
    specialMessage: string;
}

const CapAlertContainer = () => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [count, setCount] = useState(0);
    const [houseHoldId, setHouseHoldId] = useState<string | null>(null);
    const { impersonation, impersonationDetails } = useSelector(environmentSelector);
    const animationRef = useRef(null);

    const [alertData, setAlertData] = useState<IAlertData>({
        hhFirstName: '',
        hhLastName: '',
        needAttention: [],
        statues: [],
        specialMessage: '',
    });

    useEffect(() => {
        const hhId = impersonation ? impersonationDetails.householdId : sessionStorage.getItem('encHouseHoldId');
        setHouseHoldId(hhId);
        fetchData(hhId);
    }, []);

    const fetchData = async hhId => {
        setIsLoading(true);
        setCount(0);
        const data = await fetchCapAlertData(dispatch, hhId);
        setAlertData(data);
        setIsLoading(false);
        getCount(data?.needAttention?.length || 0);
    };

    const handelRedirection = btnDetails => {
        const { isImpersonationRequired } = btnDetails;
        if (isImpersonationRequired && !impersonation) {
            const hhId = sessionStorage.getItem('householdId');
            sessionStorage.setItem('isInImpersonationMode', 'true');
            sessionStorage.setItem('impersonateHHID', hhId || '');
            sessionStorage.setItem('checkDilog', '');
            const switchToModuleName = sessionStorage.getItem('switchToModuleName');
            const switchToResourceName = encodeURIComponent(`${alertData.hhFirstName} ${alertData.hhLastName}`);
            let impersonationUrl = endpoints.CAP_ALERTS_IMPERSONATION.processReplacements({
                checkConsumerView: '',
                switchToModuleName: switchToModuleName,
                switchToModuleId: houseHoldId,
                switchToResourceName: switchToResourceName,
                switchToNonDefaultURL: encodeURIComponent(btnDetails.redirectUrl),
            });
            window.location.href = impersonationUrl;
        } else if (impersonation && !isImpersonationRequired) {
            ['HHID', 'HHID2'].forEach(key => {
                localStorage.removeItem(key);
            });
            sessionStorage.setItem('isInImpersonationMode', 'false');
            sessionStorage.setItem('impersonateHHID', '');
            sessionStorage.setItem('exitImpersonation', 'true');
            window.location.href = `${impersonationDetails.switchBackLink}?requestLandingPage=${encodeURIComponent(btnDetails.redirectUrl)}`;
        } else {
            window.location.href = `/hix${btnDetails.redirectUrl}`;
        }
    };

    const toggleModal = () => {
        if (isLoading) return;

        const shoModal = !isOpen;
        setIsOpen(shoModal);
        if (shoModal && !isFirstLoad) {
            fetchData(houseHoldId);
        }
        if (isFirstLoad) {
            setIsFirstLoad(false);
        }
    };

    const getCount = targetNumber => {
        const duration = targetNumber > NUMBER.FIVE ? NUMBER.ONE_THOUSAND : NUMBER.FIVE_HUNDRED;
        cancelAnimationFrame(animationRef.current);
        const start = performance.now();
        const animate = time => {
            const progress = Math.min((time - start) / duration, 1);
            setCount(Math.floor(progress * targetNumber));
            if (progress < 1) {
                animationRef.current = requestAnimationFrame(animate);
            }
        };
        animationRef.current = requestAnimationFrame(animate);
    };

    return (
        <div id="cap-alerts-container">
            <button className={`status-button ${isLoading && 'loading'}`} onClick={toggleModal}>
                <FormattedMessage
                    id="cap.alerts.alertPill"
                    values={{
                        count: <ConditionalRendering isTrue={!isLoading && count > 0}>({count})</ConditionalRendering>,
                    }}
                />

                <ConditionalRendering isTrue={isLoading}>
                    <span className="gi-roller-spin"> </span>
                </ConditionalRendering>
            </button>
            <ConditionalRendering isTrue={isOpen && !isLoading}>
                <CapAlertModal onClose={toggleModal} alertData={alertData} handelRedirection={handelRedirection} />
            </ConditionalRendering>
        </div>
    );
};

export default CapAlertContainer;
